import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import FileUploadZone from './FileUploadZone';
import DocumentTable from './DocumentTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function ContextualDocuments({ contextType, parentContextId, title = 'Documents' }) {
  const [showUpload, setShowUpload] = useState(false);
  const queryClient = useQueryClient();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents', contextType, parentContextId],
    queryFn: async () => {
      const allDocs = await base44.entities.Document.list();
      return allDocs.filter(doc => 
        doc.context_type === contextType && 
        doc.parent_context_id === parentContextId
      );
    },
    enabled: !!parentContextId
  });

  const handleUploadComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['documents', contextType, parentContextId] });
    setShowUpload(false);
  };

  return (
    <>
      <Card className="glass-panel border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              {title}
            </CardTitle>
            <Button
              size="sm"
              onClick={() => setShowUpload(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4 text-slate-400">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 mb-3">No documents uploaded yet</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowUpload(true)}
                className="border-slate-700 text-slate-300"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload First Document
              </Button>
            </div>
          ) : (
            <DocumentTable documents={documents} showContext={false} />
          )}
        </CardContent>
      </Card>

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent data-testid="doclib-contextualdocuments-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Upload Documents
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setShowUpload(false)}
                className="h-6 w-6"
              >
                <X className="w-4 h-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <FileUploadZone
              contextType={contextType}
              parentContextId={parentContextId}
              onUploadComplete={handleUploadComplete}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}